import { useState, useEffect, useCallback } from 'react';
import { Shield, Users, Edit2, Trash2, ChevronRight, ChevronDown, ToggleLeft, ToggleRight, Crown, UserMinus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion, AnimatePresence } from 'motion/react';
import { api } from '../services/api';
import type { GroupMember } from '../types';

interface GroupRow {
  id: string;
  name: string;
  description: string | null;
  createdAt: string;
  memberCount?: number;
}

export default function SystemAdminPage() {
  const [allowRegistration, setAllowRegistration] = useState<boolean | null>(null);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  const [groups, setGroups] = useState<GroupRow[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(true);

  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [groupMembersMap, setGroupMembersMap] = useState<Record<string, GroupMember[]>>({});
  const [loadingMembersId, setLoadingMembersId] = useState<string | null>(null);

  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [savingGroupId, setSavingGroupId] = useState<string | null>(null);

  // ── load settings ──────────────────────────────────────────────────────
  useEffect(() => {
    api.admin.getSettings().then((r) => {
      setAllowRegistration(r?.allowRegistration ?? true);
    }).finally(() => setLoadingSettings(false));
  }, []);

  // ── load groups ────────────────────────────────────────────────────────
  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const r = await api.groups.listAll();
      setGroups((r?.groups as GroupRow[]) ?? []);
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  // ── toggle registration ────────────────────────────────────────────────
  const handleToggleRegistration = async () => {
    if (allowRegistration === null) return;
    setSavingSettings(true);
    try {
      const r = await api.admin.updateSettings({ allowRegistration: !allowRegistration });
      setAllowRegistration(r?.allowRegistration ?? !allowRegistration);
    } finally {
      setSavingSettings(false);
    }
  };

  // ── load group members ─────────────────────────────────────────────────
  const loadGroupMembers = async (groupId: string) => {
    if (groupMembersMap[groupId]) return;
    setLoadingMembersId(groupId);
    try {
      const r = await api.admin.listGroupMembers(groupId);
      setGroupMembersMap((prev) => ({ ...prev, [groupId]: r?.members ?? [] }));
    } finally {
      setLoadingMembersId(null);
    }
  };

  const handleToggleExpand = async (groupId: string) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      return;
    }
    setExpandedGroupId(groupId);
    await loadGroupMembers(groupId);
  };

  // ── edit group ─────────────────────────────────────────────────────────
  const startEdit = (g: GroupRow) => {
    setEditingGroupId(g.id);
    setEditName(g.name);
    setEditDesc(g.description ?? '');
  };

  const handleSaveGroup = async (groupId: string) => {
    if (!editName.trim()) return;
    setSavingGroupId(groupId);
    try {
      await api.admin.updateGroup(groupId, { name: editName.trim(), description: editDesc.trim() || null });
      setGroups((prev) => prev.map((g) => g.id === groupId ? { ...g, name: editName.trim(), description: editDesc.trim() || null } : g));
      setEditingGroupId(null);
    } finally {
      setSavingGroupId(null);
    }
  };

  // ── remove member ──────────────────────────────────────────────────────
  const handleRemoveMember = async (groupId: string, userId: string) => {
    if (!confirm('確定要將此成員從群組移除嗎？')) return;
    await api.admin.removeMember(groupId, userId);
    setGroupMembersMap((prev) => ({
      ...prev,
      [groupId]: (prev[groupId] ?? []).filter((m) => m.userId !== userId),
    }));
    setGroups((prev) => prev.map((g) => g.id === groupId ? { ...g, memberCount: (g.memberCount ?? 1) - 1 } : g));
  };

  // ── toggle member role ─────────────────────────────────────────────────
  const handleToggleMemberRole = async (groupId: string, member: GroupMember) => {
    const newRole = member.role === 'admin' ? 'member' : 'admin';
    await api.admin.updateMemberRole(groupId, member.userId, newRole);
    setGroupMembersMap((prev) => ({
      ...prev,
      [groupId]: (prev[groupId] ?? []).map((m) => m.userId === member.userId ? { ...m, role: newRole } : m),
    }));
  };

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
          <Shield className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h1 className="text-2xl font-black">系統管理</h1>
          <p className="text-sm text-muted-foreground">管理全站設定與所有群組</p>
        </div>
      </div>

      {/* Settings card */}
      <section className="rounded-2xl border border-border bg-muted/30 p-6 space-y-4">
        <h2 className="text-sm font-black uppercase tracking-widest text-muted-foreground">全站設定</h2>
        {loadingSettings ? (
          <div className="h-10 bg-muted/50 rounded-xl animate-pulse" />
        ) : (
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-sm">開放新用戶註冊</p>
              <p className="text-xs text-muted-foreground mt-0.5">關閉後，新用戶無法透過註冊頁面建立帳號</p>
            </div>
            <button
              onClick={handleToggleRegistration}
              disabled={savingSettings}
              className="transition-transform active:scale-95 disabled:opacity-50"
              aria-label="切換開放註冊"
            >
              {allowRegistration
                ? <ToggleRight className="w-10 h-10 text-primary" />
                : <ToggleLeft className="w-10 h-10 text-muted-foreground" />}
            </button>
          </div>
        )}
      </section>

      {/* Groups card */}
      <section className="rounded-2xl border border-border bg-muted/30 p-6 space-y-4">
        <h2 className="text-sm font-black uppercase tracking-widest text-muted-foreground">群組管理</h2>

        {loadingGroups ? (
          <div className="space-y-2">
            {[1, 2].map((i) => <div key={i} className="h-14 bg-muted/50 rounded-xl animate-pulse" />)}
          </div>
        ) : groups.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">尚無群組</p>
        ) : (
          <div className="space-y-2">
            <AnimatePresence initial={false}>
              {groups.map((group) => (
                <motion.div
                  key={group.id}
                  layout
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-xl border border-border bg-background overflow-hidden"
                >
                  {/* Group row */}
                  {editingGroupId === group.id ? (
                    <div className="p-4 space-y-3">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="群組名稱"
                        className="h-9 rounded-lg"
                        autoFocus
                      />
                      <Input
                        value={editDesc}
                        onChange={(e) => setEditDesc(e.target.value)}
                        placeholder="描述（可選）"
                        className="h-9 rounded-lg"
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleSaveGroup(group.id)}
                          disabled={!editName.trim() || savingGroupId === group.id}
                          className="flex-1 h-8 rounded-lg text-xs"
                        >
                          {savingGroupId === group.id ? '儲存中…' : '儲存'}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingGroupId(null)}
                          className="flex-1 h-8 rounded-lg text-xs"
                        >
                          取消
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 flex items-center gap-3">
                      <button
                        onClick={() => handleToggleExpand(group.id)}
                        className="flex-1 flex items-center gap-3 text-left"
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Users className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-sm truncate">{group.name}</p>
                          {group.description && (
                            <p className="text-xs text-muted-foreground truncate">{group.description}</p>
                          )}
                          {group.memberCount !== undefined && (
                            <p className="text-[10px] text-muted-foreground">{group.memberCount} 位成員</p>
                          )}
                        </div>
                        {loadingMembersId === group.id ? (
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                        ) : expandedGroupId === group.id ? (
                          <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        )}
                      </button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startEdit(group)}
                        className="h-8 w-8 p-0 rounded-lg flex-shrink-0"
                        title="編輯群組"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}

                  {/* Expanded member list */}
                  <AnimatePresence initial={false}>
                    {expandedGroupId === group.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-border/60 px-4 pb-4 pt-3 space-y-2 bg-muted/20">
                          {(groupMembersMap[group.id] ?? []).length === 0 ? (
                            <p className="text-xs text-muted-foreground py-2 text-center">此群組暫無成員</p>
                          ) : (
                            (groupMembersMap[group.id] ?? []).map((m) => (
                              <div key={m.userId} className="flex items-center gap-3 py-1.5 rounded-lg px-2 hover:bg-muted/50 transition-colors">
                                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-black text-primary flex-shrink-0">
                                  {m.nickname?.[0] ?? '?'}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-bold truncate">{m.nickname}</p>
                                  <p className="text-[10px] text-muted-foreground truncate">{m.email}</p>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {m.role === 'admin' && (
                                    <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-600 flex items-center gap-0.5">
                                      <Crown className="w-2.5 h-2.5" /> 管理員
                                    </span>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleToggleMemberRole(group.id, m)}
                                    className="h-7 w-7 p-0 rounded-lg"
                                    title={m.role === 'admin' ? '降為一般成員' : '升為管理員'}
                                  >
                                    <Crown className={`w-3 h-3 ${m.role === 'admin' ? 'text-amber-500' : 'text-muted-foreground'}`} />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleRemoveMember(group.id, m.userId)}
                                    className="h-7 w-7 p-0 rounded-lg hover:bg-destructive/15 hover:text-destructive"
                                    title="移除成員"
                                  >
                                    <UserMinus className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </section>
    </div>
  );
}
